import React from "react";
import ReactApexChart from "react-apexcharts";
import { buildChartData, getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownList from "./MemberBreakdownList";

const TrendBadge = ({ trend }) => {
  if (!trend) return null;
  const { percent, direction } = trend;
  const cls = `widgetCardTrendBadge widgetCardTrendBadge${
    direction === "up" ? "Up" : direction === "down" ? "Down" : "Neutral"
  }`;
  const arrow = direction === "up" ? "↑" : direction === "down" ? "↓" : "—";
  const sign = direction === "up" ? "+" : direction === "down" ? "-" : "";
  const label = percent != null ? `${sign}${Math.abs(percent).toFixed(0)}% vs prev. period` : "";
  return (
    <div className={cls}>
      <span className="trendArrow">{arrow}</span>
      <span>{label}</span>
    </div>
  );
};

const buildChartOptions = (labels, isSingleDay, { max, tickAmount }, metricLabel, isBar = false) => ({
  chart: {
    type: isBar ? "bar" : "area",
    toolbar: { show: false },
    animations: { enabled: false },
    selection: { enabled: false },
    zoom: { enabled: false }
  },
  ...(isBar
    ? { plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } } }
    : {
        stroke: { curve: "smooth", width: 2 },
        markers: {
          size: isSingleDay ? 5 : 3,
          colors: ["#3953fb"],
          strokeColors: "#fff",
          strokeWidth: 2,
          hover: { size: isSingleDay ? 6 : 4 }
        },
        fill: {
          type: "solid",
          opacity: 0.2
        }
      }),
  colors: ["#3953fb"],
  dataLabels: { enabled: false },
  xaxis: {
    categories: labels,
    labels: { show: isSingleDay, style: { fontSize: "11px" } },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false }
  },
  yaxis: {
    min: 0,
    max,
    tickAmount,
    labels: { show: isSingleDay, style: { fontSize: "11px" } }
  },
  grid: {
    borderColor: "#e8ebee",
    strokeDashArray: 3,
    padding: { left: 0, right: 0, top: 0, bottom: -10 },
    yaxis: { lines: { show: true } },
    xaxis: { lines: { show: false } }
  },
  tooltip: {
    y: {
      title: { formatter: () => (metricLabel ? `${metricLabel}:` : "") },
      formatter: val => Math.round(val).toLocaleString()
    }
  }
});

const CountWidget = ({ value, trend, series, dateFilter, customRange, metricLabel, unit, memberBreakdown, breakdownLoading, visualization }) => {
  const displayValue = value != null ? value.toLocaleString() : "—";
  const isKpiOnly = visualization === "KPI";
  const isBar = visualization === "BAR";
  const hasSeries = !isKpiOnly && Array.isArray(series) && series.length > 0;

  const chartData = hasSeries ? buildChartData(series, dateFilter, customRange) : [];
  const isSingleDay = chartData.length === 1;
  const labels = chartData.map(d => d.label);
  const values = chartData.map(d => d.value || 0);
  const apexSeries = chartData.length > 0 ? [{ name: "Count", data: values }] : [];
  const options = buildChartOptions(labels, isSingleDay, getNiceMax(Math.max(...values, 1)), metricLabel, isBar);

  return (
    <>
      <div className="widgetCardValue">
        {displayValue}
        {unit && <span className="widgetCardValueUnit">{unit}</span>}
      </div>
      <TrendBadge trend={trend} />
      <MemberBreakdownList rows={memberBreakdown} loading={breakdownLoading} />
      {apexSeries.length > 0 && (
        <div className="rateWidgetChartWrap">
          <ReactApexChart
            options={options}
            series={apexSeries}
            type={isBar ? "bar" : "area"}
            height={140}
          />
        </div>
      )}
    </>
  );
};

export default CountWidget;
